/**
 * Geneva Functions - Shared utilities for Geneva schema and object generation
 * 
 * This module contains shared functions that can be used by both React client
 * and Firebase Functions for Geneva record processing, inheritance resolution,
 * and complete XML object generation.
 * 
 * Environment compatibility: Browser (React) and Node.js (Firebase Functions)
 */

import { CutoffSpec, PortfolioPositionWithCustodianAccountsResponse } from "../webapp/src/types";

// Type definitions - these should match the React types
// For now, defining them here to avoid circular dependencies
// In the future, these could be moved to a separate shared types file

export interface BaseField {
  xmlTag: string;
  type: string;
  required: boolean;
  multiple?: boolean;
  description?: string;
  quickImportColumn?: string | null;
  references?: string | null;
  links: string[]; // Required to match React types
}

export interface ArrayFieldGroup {
  arrayName: string;
  fields: BaseField[];
}

export interface BaseRecord {
  record: string;
  fields: BaseField[];
}

export interface LookupConfig {
  route: string;
  key: string;
  label: string;
  dependsOn?: string;
}

export interface ComputedConfig {
  expr: string;
}

export interface DefaultArrayFieldSettings {
  required?: boolean;
  hidden?: boolean;
  readOnly?: boolean;
}

export interface DefaultArrayItem {
  id?: string;
  values: Record<string, unknown>; // Field values (fieldName -> value)
  computedExpressions?: Record<string, string>; // Field expressions (fieldName -> expression)
  label?: string; // Optional label for the default item (e.g., "Commission", "Other Fees")
  fieldSettings?: Record<string, DefaultArrayFieldSettings>; // Row-level field settings
}

export interface ValidationRule {
  expr: string; // Expression that should evaluate to true
  message?: string; // Error or warning message
  level?: 'error' | 'warning'; // Default 'error'
}

export interface FieldOverride {
  hidden?: boolean;
  required?: boolean;
  default?: unknown;
  readOnly?: boolean;
  help?: string;
  options?: string[];
  lookup?: LookupConfig;
  computed?: ComputedConfig;
  // UI-only: mark this field as editable per additional transaction in the UI
  // Does not change schema semantics; used for client/server merging logic
  multiTxn?: boolean;
  // Array group configuration (applies to first field in array group)
  defaultArrayItems?: DefaultArrayItem[]; // Pre-populated array items with expressions
  // Validation configuration (optional)
  // One or more expression rules that should evaluate to true to pass
  validate?: ValidationRule | ValidationRule[];
  // Conditionally require a field when this expression evaluates to true
  requiredWhen?: string;
}

export interface MultiRecordConfig {
  recordName: string;
  fieldOverrides?: Record<string, FieldOverride>;
  order?: number;
}

export interface VirtualRecord {
  base?: string;
  baseRecords?: string[];
  multiRecordConfig?: MultiRecordConfig[];
  label: string;
  enabled: boolean;
  fieldOverrides: Record<string, FieldOverride>;
  createdAt: number;
  createdBy: string;
  isMultiRecord?: boolean;
}

export interface TemplateDoc {
  schemaVersion: string;
  enabledRecords: Record<string, boolean>;
  fieldOverrides: Record<string, Record<string, FieldOverride>>;
  recordOrder?: string[];
  virtualRecords?: Record<string, VirtualRecord>;
  recordLookupFields?: Record<string, Array<{
    name: string; // Field name in the lookup table (e.g., "UDF.CCY")
    alias?: string; // Optional alias for the field in the result
  }>>; // recordName -> additional fields to fetch when this record is used as lookup
  updatedAt: number;
  updatedBy: string;
  name: string;
  description?: string;
  // Optional record-level validations per record (real or virtual)
  recordValidations?: Record<string, ValidationRule[]>;
  // Reusable validation templates (named rules with optional async and params)
  validationTemplates?: Record<string, {
    expr: string;
    async?: boolean;
    params?: string[];
  }>;
}

export interface BaseSchema {
  sourceFile?: string;
  generatedAt?: string;
  stats?: {
    recordCount: number;
    fieldCount: number;
  };
  records: BaseRecord[];
}

// Small epsilon to tolerate floating point noise in balance checks
// Example: 623.159999999999 - 623.16 ≈ -1.14e-13 should be considered zero
export const FLOAT_TOLERANCE_EPSILON = 1e-8;

/**
 * Recursively resolves field overrides for a record, following the inheritance chain
 * @param recordId The record ID to resolve (can be real or virtual)
 * @param template The template document containing virtual records and field overrides
 * @param visited Set of visited record IDs for circular reference detection
 * @returns Merged field overrides from the entire inheritance chain
 */
export const resolveFieldOverridesRecursively = (
  recordId: string,
  template: TemplateDoc,
  visited: Set<string> = new Set()
): Record<string, FieldOverride> => {
  // Detect circular references
  if (visited.has(recordId)) {
    const chain = [...visited, recordId].join(' → ');
    throw new Error(`Circular inheritance detected: ${chain}`);
  }

  // Add current record to visited set
  const newVisited = new Set(visited);
  newVisited.add(recordId);

  // Check if this is a virtual record
  const virtualRecord = template.virtualRecords?.[recordId];

  if (!virtualRecord || !virtualRecord.base) {
    // This is a real/base record, return its direct field overrides
    return template.fieldOverrides[recordId] || {};
  }

  // This is a virtual record, recursively resolve parent's overrides first
  const parentOverrides = resolveFieldOverridesRecursively(
    virtualRecord.base,
    template,
    newVisited
  );

  // Merge parent overrides with current virtual record overrides
  // Virtual record overrides take precedence over parent overrides
  return {
    ...parentOverrides,
    ...virtualRecord.fieldOverrides || {}
  };
};

/**
 * Recursively resolves field overrides for a multi-record configuration, handling inheritance
 * @param virtualRecordId The virtual multi-record ID 
 * @param baseRecordName The base record name within the multi-record config
 * @param template The template document containing virtual records and field overrides
 * @param visited Set of visited record IDs for circular reference detection
 * @returns Merged field overrides from inheritance chain plus multi-record specific overrides
 */
export const resolveMultiRecordFieldOverrides = (
  virtualRecordId: string,
  baseRecordName: string,
  template: TemplateDoc,
  visited: Set<string> = new Set()
): Record<string, FieldOverride> => {
  // Get the virtual record
  const virtualRecord = template.virtualRecords?.[virtualRecordId];
  if (!virtualRecord?.isMultiRecord || !virtualRecord.multiRecordConfig) {
    return {};
  }

  // Find the config for this base record
  const recordConfig = virtualRecord.multiRecordConfig.find(c => c.recordName === baseRecordName);
  if (!recordConfig) {
    return {};
  }

  // Start with the base record's own overrides (if any)
  const baseOverrides = resolveFieldOverridesRecursively(baseRecordName, template, visited);

  // Layer on the virtual record's global field overrides
  const globalOverrides = virtualRecord.fieldOverrides || {};

  // Finally, layer on the specific multi-record config overrides
  const configOverrides = recordConfig.fieldOverrides || {};

  // Merge in order of precedence: base < global < config-specific
  return {
    ...baseOverrides,
    ...globalOverrides,
    ...configOverrides
  };
};

/**
 * Gets the full inheritance chain for a record (useful for debugging and UI display)
 * @param recordId The record ID to trace
 * @param template The template document
 * @returns Array of record IDs in inheritance order (child to parent)
 */
export const getInheritanceChain = (
  recordId: string,
  template: TemplateDoc
): string[] => {
  const chain: string[] = [];
  let currentRecordId = recordId;
  const visited = new Set<string>();

  while (currentRecordId) {
    // Detect circular references
    if (visited.has(currentRecordId)) {
      throw new Error(`Circular inheritance detected in chain: ${chain.join(' → ')}`);
    }

    visited.add(currentRecordId);
    chain.push(currentRecordId);

    // Get the parent record
    const virtualRecord = template.virtualRecords?.[currentRecordId];
    currentRecordId = virtualRecord?.base || '';
  }

  return chain;
};

/**
 * Checks if a record exists (either as real record in schema or virtual record in template)
 * @param recordId The record ID to check
 * @param template The template document
 * @param schema The base schema (optional)
 * @returns Boolean indicating if the record exists
 */
export const recordExists = (
  recordId: string,
  template: TemplateDoc,
  schema?: BaseSchema
): boolean => {
  // Check if it's a virtual record
  if (template.virtualRecords?.[recordId]) {
    return true;
  }

  // Check if it's a real record in the schema
  if (schema?.records.some(r => r.record === recordId)) {
    return true;
  }

  // Check if it's a real record in field overrides (fallback)
  return template.fieldOverrides[recordId] !== undefined;
};

/**
 * Helper function to set a nested property using dot notation
 * @param obj Target object
 * @param path Dot notation path (e.g., "UDF.CCY")
 * @param value Value to set
 */
const setNestedProperty = (obj: Record<string, any>, path: string, value: any) => {
  const keys = path.split('.');
  let current = obj;

  // Navigate to the parent object
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  // Set the final property
  current[keys[keys.length - 1]] = value;
};

// No complex conversion needed anymore - removed createLookupFieldContext

/**
 * Creates a safe evaluation context with the provided data
 * @param formData User form data
 * @param currentResult Current result being built
 * @param recordScopes Optional record-scoped data for multi-record forms
 * @returns Safe evaluation context with helper functions
 */
const createEvaluationContext = (
  formData: Record<string, any>,
  currentResult: Record<string, any>,
  recordScopes?: Record<string, Record<string, any>>
) => {
  const allData = { ...formData, ...currentResult };

  const context: Record<string, any> = {
    // All form data and current results - this includes all Geneva lookup data with underscore names
    ...allData,

    // Helper functions that can be used in expressions
    Date: {
      now: () => Date.now(),
      today: () => new Date().toISOString().split('T')[0],
      parse: (dateStr: string) => new Date(dateStr).getTime(),
      addDays: (dateStr: string | Date, days: number) => {
        const date = new Date(dateStr);
        date.setDate(date.getDate() + days);
        return date.toISOString();
      },
      addMonths: (dateStr: string | Date, months: number) => {
        const date = new Date(dateStr);
        date.setMonth(date.getMonth() + months);
        return date.toISOString();
      },
      addYears: (dateStr: string | Date, years: number) => {
        const date = new Date(dateStr);
        date.setFullYear(date.getFullYear() + years);
        return date.toISOString();
      }
    },

    // Math helpers
    Math: {
      abs: Math.abs,
      ceil: Math.ceil,
      floor: Math.floor,
      round: Math.round,
      min: Math.min,
      max: Math.max
    },

    // String helpers
    String: {
      upper: (str: string) => String(str).toUpperCase(),
      lower: (str: string) => String(str).toLowerCase(),
      trim: (str: string) => String(str).trim()
    },

    // Conditional helper
    IF: (condition: boolean, trueValue: any, falseValue: any) => condition ? trueValue : falseValue,

    // Geneva-specific constants
    CALC: 'CALC',
    NULL: null,
    EMPTY: ''
  };

  // Add record-scoped data for multi-record forms (e.g., Transfer.EventDate, SpotFX.Amount)
  if (recordScopes) {
    Object.entries(recordScopes).forEach(([recordName, recordData]) => {
      context[recordName] = recordData || {};
    });
  }

  return context;
};

/**
 * Safely evaluates a computed expression using eval with a controlled context
 * @param expr The expression to evaluate
 * @param formData User form data
 * @param currentResult Current result being built (for referencing other computed fields)
 * @param recordScopes Optional record-scoped data for multi-record forms
 * @returns Computed value
 */
const evaluateComputedExpression = (
  expr: string,
  formData: Record<string, any>,
  currentResult: Record<string, any>,
  recordScopes?: Record<string, Record<string, any>>
): any => {
  try {
    // Handle simple special cases first (for performance)
    if (expr === 'CALC') return 'CALC';
    if (expr === 'NULL') return null;
    if (expr === 'EMPTY') return '';

    // Clean up expression - remove trailing semicolon and whitespace
    let cleanExpr = expr.trim();
    if (cleanExpr.endsWith(';')) {
      cleanExpr = cleanExpr.slice(0, -1);
    }

    // Handle direct field references (like "EventDate")
    if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(cleanExpr)) {
      const value = formData[cleanExpr] || currentResult[cleanExpr];
      if (value !== undefined) {
        return value;
      }
    }

    // Handle Date.now() specially for compatibility
    if (cleanExpr.includes('Date.now()')) {
      return eval(cleanExpr.replace(/Date\.now\(\)/g, String(Date.now())));
    }

    // Create safe evaluation context with all available data
    const context = createEvaluationContext(formData, currentResult, recordScopes);

    // For expressions like "new Date(EventDate).toLocaleString()", we need to make
    // the native Date constructor and other globals available
    const evalContext = {
      ...context,
      Date: Date, // Native Date constructor
      String: String, // Native String constructor
      Number: Number, // Native Number constructor
      Math: Math, // Native Math object
      console: console // Allow logging to surface in environment
    };

    // Build the function body with the context
    const contextKeys = Object.keys(evalContext);
    const contextValues = Object.values(evalContext);

    // Debug logging for complex expressions
    if (cleanExpr.includes('.')) {
      console.log('Evaluating expression with dot notation:', cleanExpr);
      console.log('Available context keys:', Object.keys(context));

      // Check if we're accessing a lookup field
      const parts = cleanExpr.split('.');
      if (parts.length >= 2) {
        const baseField = parts[0];
        console.log(`Checking for ${baseField} in context:`, context[baseField]);
      }
    }

    // Decide how to build the function body
    // If expression appears complex (multi-line, semicolons, or statements), don't prepend return
    const isComplex = /[;\n]|\b(if|const|let|var|function|=>)\b/.test(cleanExpr);
    let functionBody: string;
    if (isComplex) {
      functionBody = cleanExpr; // user is responsible for return statements
    } else {
      // Simple expression: ensure we return its value
      functionBody = /^\s*return\b/.test(cleanExpr) ? cleanExpr : `return ${cleanExpr}`;
    }

    // Create a function with the context variables in scope
    const func = new Function(...contextKeys, functionBody);

    // Execute the function with the context values
    const result = func(...contextValues);

    console.log(`Expression "${cleanExpr}" evaluated to:`, result);

    return result;
  } catch (error) {
    console.warn('Primary evaluation failed for expression:', expr);
    console.warn('Error details:', error);

    // Log available context for debugging
    console.log('Context dump for debugging:');
    console.log('- Form data keys:', Object.keys(formData));
    console.log('- Current result keys:', Object.keys(currentResult));
    if (recordScopes) {
      console.log('- Record scopes:', Object.keys(recordScopes));
    }

    // Check for specific common issues
    if (expr.includes('.')) {
      const parts = expr.split('.');
      const baseField = parts[0];
      console.log(`Expression uses dot notation. Checking for '${baseField}'...`);

      // Create evaluation context to check field availability
      const debugContext = createEvaluationContext(formData, currentResult, recordScopes);

      // Check if base field exists
      if (!debugContext[baseField]) {
        console.error(`ERROR: Field '${baseField}' not found in context!`);
        console.log('Available fields in context:', Object.keys(debugContext).filter(k => !k.startsWith('_')));

        // Check if it might be a lookup field that wasn't fetched
        const lookupFields = Object.keys(formData).filter(k => k.startsWith(`${baseField}_`));
        if (lookupFields.length > 0) {
          console.log(`Found underscore-prefixed fields: ${lookupFields.join(', ')}`);
          console.log('These suggest lookup data exists but may not be properly structured.');
        }
      } else {
        console.log(`Found '${baseField}' in context:`, debugContext[baseField]);
      }
    }

    // Try a simpler eval approach as fallback
    try {
      // Create a simple context with form data
      const allData = { ...formData, ...currentResult };
      let simpleExpr = expr.trim();
      if (simpleExpr.endsWith(';')) {
        simpleExpr = simpleExpr.slice(0, -1);
      }

      // Remove 'return' if present
      simpleExpr = simpleExpr.replace(/^return\s+/, '');

      console.log('Attempting fallback evaluation with simple expression:', simpleExpr);

      // Replace field references with actual values
      Object.keys(allData).forEach(fieldName => {
        const value = allData[fieldName];
        if (value !== undefined && value !== null) {
          // For dates, keep them as dates; for strings, quote them; for numbers, use as-is
          let replacement;
          if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
            // If it's a date or date string, create a Date object
            replacement = `new Date("${value}")`;
          } else if (typeof value === 'string') {
            replacement = `"${value.replace(/"/g, '\\"')}"`;
          } else {
            replacement = String(value);
          }

          // Replace whole word matches only
          const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
          simpleExpr = simpleExpr.replace(regex, replacement);
        }
      });

      // Use eval as last resort
      console.log('Evaluating fallback expression:', simpleExpr);
      const result = eval(simpleExpr);
      console.log('Fallback evaluation succeeded with result:', result);
      return result;
    } catch (fallbackError) {
      console.error('Fallback evaluation also failed:', fallbackError);
      console.error('Fallback error message:', fallbackError instanceof Error ? fallbackError.message : String(fallbackError));
    }

    // Final fallback: try direct field lookup
    if (formData[expr] !== undefined) {
      console.log('Found expression as direct field in formData:', formData[expr]);
      return formData[expr];
    }

    if (currentResult[expr] !== undefined) {
      console.log('Found expression as direct field in currentResult:', currentResult[expr]);
      return currentResult[expr];
    }

    // If we get here, the expression failed to evaluate
    // Create a comprehensive error message
    const errorDetails = {
      expression: expr,
      primaryError: error instanceof Error ? error.message : String(error),
      availableFormFields: Object.keys(formData).slice(0, 20), // First 20 fields
      availableResultFields: Object.keys(currentResult),
      lookupFields: Object.keys(formData).filter(k => k.includes('_')),
      hasRecordScopes: !!recordScopes
    };

    const errorMessage = `Failed to evaluate expression: "${expr}"\n` +
      `Primary error: ${errorDetails.primaryError}\n` +
      `Available form fields: ${errorDetails.availableFormFields.join(', ')}${formData.length > 20 ? '...' : ''}\n` +
      `Available result fields: ${errorDetails.availableResultFields.join(', ')}\n` +
      `Lookup fields found: ${errorDetails.lookupFields.join(', ') || 'none'}`;

    throw new Error(errorMessage);
  }
};

/**
 * Creates record scopes for multi-record forms to enable cross-record field references
 * @param multiRecordFormData Form data organized by record (e.g., { Transfer: {...}, SpotFX: {...} })
 * @returns Record scopes that can be used in expressions
 */
export const createRecordScopes = (
  multiRecordFormData: Record<string, Record<string, any>>
): Record<string, Record<string, any>> => {
  return multiRecordFormData;
};

/**
 * Evaluates expressions with support for both sync and async operations
 * @param expr The expression to evaluate
 * @param context Data context for evaluation
 * @param recordScopes Optional record-scoped data for multi-record forms
 * @param asyncFunctions Optional async functions available in the expression context
 * @returns Promise with evaluation result including success/error info
 */
export const safeEvaluateExpression = async (
  expr: string,
  context: Record<string, any> = {},
  recordScopes?: Record<string, Record<string, any>>,
  asyncFunctions?: Record<string, (...args: any[]) => Promise<any>>
): Promise<{ success: boolean; result: any; error?: string }> => {
  console.log(`[safeEvaluateExpression] Starting evaluation of: ${expr}`);
  console.log(`[safeEvaluateExpression] Has async functions:`, !!asyncFunctions);

  try {
    // For simple non-async expressions, try sync evaluation first for performance
    const hasAsyncKeywords = /\b(await|async)\b/.test(expr);
    console.log(`[safeEvaluateExpression] Has async keywords:`, hasAsyncKeywords);

    if (!hasAsyncKeywords && !asyncFunctions) {
      // Use sync evaluation for simple expressions
      try {
        const result = evaluateComputedExpression(expr, context, {}, recordScopes);
        return {
          success: true,
          result
        };
      } catch (syncError) {
        // Fall through to async evaluation if sync fails
        console.debug('Sync evaluation failed, trying async:', syncError);
      }
    }

    // Clean up expression - remove trailing semicolon and whitespace
    let cleanExpr = expr.trim();
    if (cleanExpr.endsWith(';')) {
      cleanExpr = cleanExpr.slice(0, -1);
    }

    // Create evaluation context with async functions
    const evalContext = createEvaluationContext(context, {}, recordScopes);

    // Add async functions to context
    if (asyncFunctions) {
      console.log(`[safeEvaluateExpression] Adding async functions to context:`, Object.keys(asyncFunctions));
      Object.assign(evalContext, asyncFunctions);
    }

    // Build the async function body
    const contextKeys = Object.keys(evalContext);
    const contextValues = Object.values(evalContext);

    console.log(`[safeEvaluateExpression] Context keys:`, contextKeys.filter(k => !['Date', 'Math', 'String', 'IF', 'CALC', 'NULL', 'EMPTY'].includes(k)));

    // Check if expression is complex (multi-line, semicolons, or statements)
    const isComplex = /[;\n]|\b(if|const|let|var|function|=>)\b/.test(cleanExpr);
    let functionBody: string;
    if (isComplex) {
      functionBody = cleanExpr; // user is responsible for return statements
    } else {
      // Simple expression: ensure we return its value
      functionBody = /^\s*return\b/.test(cleanExpr) ? cleanExpr : `return ${cleanExpr}`;
    }

    console.log(`[safeEvaluateExpression] Function body to execute:`, functionBody);

    // Create an async function with the context variables in scope
    const AsyncFunction = Object.getPrototypeOf(async function () { }).constructor;
    const func = new AsyncFunction(...contextKeys, functionBody);

    console.log(`[safeEvaluateExpression] Executing async function...`);
    // Execute the async function with the context values
    const result = await func(...contextValues);
    console.log(`[safeEvaluateExpression] Execution result:`, result);

    return {
      success: true,
      result
    };
  } catch (error) {
    console.error(`[safeEvaluateExpression] Error evaluating expression:`, error);
    console.error(`[safeEvaluateExpression] Expression was:`, expr);
    console.error(`[safeEvaluateExpression] Error message:`, error instanceof Error ? error.message : String(error));
    return {
      success: false,
      result: expr,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

/**
 * Normalizes validation rules to an array
 */
const normalizeRules = (rules?: ValidationRule | ValidationRule[]): ValidationRule[] => {
  if (!rules) return [];
  return Array.isArray(rules) ? rules : [rules];
};



/**
 * Creates a validation context with helper functions for portfolio checks
 * This function creates async validation functions that can be used in both:
 * - Client-side (via Firebase callable functions)
 * - Server-side (via direct database access)
 * 
 * @param firebaseFunctionsOrFetcher Either:
 *   - On client: Object containing Firebase callable functions
 *   - On server: Direct function to fetch portfolio position data
 * @returns Object with async validation functions
 */
export const createPortfolioValidationFunctions = (
  firebaseFunctionsOrFetcher: Record<string, (...args: any[]) => Promise<any>> | ((portfolioCode: string, date?: string) => Promise<any>)
): Record<string, (...args: any[]) => Promise<any>> => {
  // Check if we're on the server (direct function) or client (Firebase functions object)
  const isServerSide = typeof firebaseFunctionsOrFetcher === 'function';

  // Helper to get portfolio position data
  const getPortfolioPositionData = async (portfolioCode: string, date?: string): Promise<PortfolioPositionWithCustodianAccountsResponse> => {
    if (isServerSide) {
      // Server-side: direct function call
      return await (firebaseFunctionsOrFetcher as Function)(portfolioCode, date);
    } else {
      // Client-side: Firebase callable function
      const firebaseFunctions = firebaseFunctionsOrFetcher as Record<string, Function>;
      if (!firebaseFunctions.getPortfolioPositionWithCustodianAccounts) {
        throw new Error('getPortfolioPositionWithCustodianAccounts function not provided');
      }
      // Must set custom_date so the callable honors `date` (same as PortfolioPositionTable)
      const request: { portfolioCode: string; date?: string; custom_date?: boolean } = { portfolioCode };
      if (date) {
        request.date = date;
        request.custom_date = true;
      }
      let result = await firebaseFunctions.getPortfolioPositionWithCustodianAccounts(request);
      console.log(`[thisStupidFunction] Result:`, result);
      return result.data;

    }
  };

  return {
    // Wrapper for getPortfolioPositionWithCustodianAccounts
    getPortfolioPosition: async (portfolioCode: string, date?: string) => {
      return await getPortfolioPositionData(portfolioCode, date);
    },

    // Check if balance would go negative after transaction
    checkBalanceAfterTransaction: async (
      portfolioCode: string,
      custodianAccount: string,
      transactionQuantity: number,
      investment?: string,
      eventDate?: string,
      tolerance: number = FLOAT_TOLERANCE_EPSILON
    ) => {

      const result = await getPortfolioPositionData(portfolioCode, eventDate);

      console.log(`[checkTransactionWouldCauseNegativeBalance] Result:`, result);
      if (!result.rows || result.rows.length === 0) {
        // If no position exists and transaction is negative, it would go below 0
        console.log(`No rows whatsoever for portfolio ${portfolioCode} for date ${eventDate}`, result);
        throw new Error(`No rows whatsoever for portfolio ${portfolioCode} for date ${eventDate}`);
      }

      // Find the specific custodian account
      const accountPosition = result.rows.find((pos) =>
        pos.CUSTODIANACCOUNT === custodianAccount && pos.CODE === investment
      );

      if (!accountPosition) {
        console.log(`no position for ${investment} in ${custodianAccount} on ${eventDate} for portfolio ${portfolioCode}`);
        // If no position exists and transaction is negative, it would go below 0
        throw new Error(`no position for ${investment} in ${custodianAccount} on ${eventDate} for portfolio ${portfolioCode}`);
      }

      // Get current balance
      const currentBalance = accountPosition.QTYEND || 0;

      const numericBalance = typeof currentBalance === 'string' ? parseFloat(currentBalance) : currentBalance;
      const numericTransaction = typeof transactionQuantity === 'string' ? parseFloat(transactionQuantity) : transactionQuantity;

      console.log(`[checkTransactionWouldCauseNegativeBalance] Current balance: ${numericBalance}, Transaction quantity: ${numericTransaction}`);
      // Check if balance after transaction would be negative
      // Use tolerance to account for floating point precision issues
      const delta = numericBalance - numericTransaction;
      const willNotGoNegative = delta >= -Math.abs(tolerance);


      if (willNotGoNegative) {
        return willNotGoNegative;
      }
      //Yousef:  ok this is stupid.  I don't know why I'm throwing here because
      //         there could be a instance where it's only a warning.  Problem for future me.
      throw new Error(`Balance would go negative after transaction: ${numericBalance} - ${numericTransaction} = ${delta}`);
    }
  };
};

export interface RecordValidationResult {
  isValid: boolean;
  fieldErrors: Record<string, string[]>;
  recordErrors: string[];
  warnings: string[];
}

/**
 * Options for validation functions
 */
export interface ValidationOptions {
  // Async functions available during validation (e.g., Firebase functions)
  asyncFunctions?: Record<string, (...args: any[]) => Promise<any>>;
  // Validation template instances with their parameters
  templateInstances?: Record<string, { templateName: string; params: Record<string, any> }>;
}

/**
 * Evaluates a validation template with parameters
 */
const evaluateValidationTemplate = async (
  templateName: string,
  params: Record<string, any>,
  template: TemplateDoc,
  context: Record<string, any>,
  recordScopes?: Record<string, Record<string, any>>,
  asyncFunctions?: Record<string, (...args: any[]) => Promise<any>>
): Promise<{ success: boolean; result: any; error?: string }> => {
  const validationTemplate = template.validationTemplates?.[templateName];
  if (!validationTemplate) {
    return {
      success: false,
      result: false,
      error: `Validation template '${templateName}' not found`
    };
  }

  // Create context with template parameters
  const fullContext = {
    ...context,
    ...params
  };

  // Always use the unified async evaluation function
  console.log(`[evaluateValidationTemplate] Template: ${templateName}`);
  console.log(`[evaluateValidationTemplate] Expression: ${validationTemplate.expr}`);
  console.log(`[evaluateValidationTemplate] Parameters:`, params);
  console.log(`[evaluateValidationTemplate] Has async functions:`, !!asyncFunctions);
  if (asyncFunctions) {
    console.log(`[evaluateValidationTemplate] Available async functions:`, Object.keys(asyncFunctions));
  }

  const result = await safeEvaluateExpression(
    validationTemplate.expr,
    fullContext,
    recordScopes,
    asyncFunctions
  );

  console.log(`[evaluateValidationTemplate] Result:`, result);
  return result;
};

/**
 * Validates a single record's form data using field overrides and record-level validations
 * Now supports async validation templates
 */
export const validateRecordData = async (
  recordId: string,
  formData: Record<string, any>,
  template: TemplateDoc,
  schema: BaseSchema,
  recordScopes?: Record<string, Record<string, any>>,
  options?: ValidationOptions
): Promise<RecordValidationResult> => {
  const result: RecordValidationResult = {
    isValid: true,
    fieldErrors: {},
    recordErrors: [],
    warnings: [],
  };

  // Resolve overrides for this record (handles virtual inheritance)
  const resolvedOverrides = resolveFieldOverridesRecursively(recordId, template);

  // Build evaluation base context
  const baseContext = { ...formData };

  // Validate required/requiredWhen and field-level rules
  for (const [fieldName, override] of Object.entries(resolvedOverrides)) {
    const errors: string[] = [];
    const value = formData[fieldName];

    // required or requiredWhen
    let isRequired = !!override.required && !override.hidden;
    //make sure it's not part of ExternalSystemReferenceID, KeyValue, or UserTranId1
    //those are usually filled automatically by the system
    if (['ExternalSystemReferenceID', 'KeyValue', 'UserTranId1'].includes(fieldName)) {
      isRequired = false;
    }
    if (override.requiredWhen) {
      const guard = await safeEvaluateExpression(override.requiredWhen, baseContext, recordScopes);
      if (guard.success && !!guard.result) {
        isRequired = true;
      }
    }
    const isEmpty = value === undefined || value === null || value === '';
    if (isRequired && isEmpty) {
      errors.push(`${fieldName} is required`);
    }

    // field-level validate rules
    const rules = normalizeRules(override.validate);
    for (const rule of rules) {
      // Check if this is a template reference
      // Allow template names with spaces and symbols up to the first '('
      const templateMatch = rule.expr.trim().match(/^@(.+?)\s*\((.*)\)\s*$/);
      if (templateMatch) {
        const [, rawTemplateName, rawParamsStr] = templateMatch;
        const templateName = rawTemplateName.trim();
        const paramsStr = (rawParamsStr || '').trim();
        try {
          // Parse parameters (simple key=value pairs)
          const params: Record<string, any> = {};
          if (paramsStr) {
            const paramPairs = paramsStr.split(',');
            for (const pair of paramPairs) {
              const [key, value] = pair.split('=').map(s => s.trim());
              // Evaluate the parameter value in context
              const paramResult = await safeEvaluateExpression(value, baseContext, recordScopes);
              params[key] = paramResult.success ? paramResult.result : value;
            }
          }

          // Include field value in params
          params.value = value;
          params.Value = value;

          const outcome = await evaluateValidationTemplate(
            templateName,
            params,
            template,
            baseContext,
            recordScopes,
            options?.asyncFunctions
          );

          if (!(outcome.success && !!outcome.result)) {
            const msg = rule.message || `${fieldName} failed validation: ${outcome.error || 'Invalid'}`;
            if (rule.level === 'warning') {
              result.warnings.push(msg);
            } else {
              errors.push(msg);
            }
          }
        } catch (err) {
          errors.push(`Failed to evaluate template ${templateName}: ${err instanceof Error ? err.message : String(err)}`);
        }
      } else {
        // Regular expression validation
        const context = { ...baseContext, Value: value, value };
        const outcome = await safeEvaluateExpression(rule.expr, context, recordScopes);
        if (!(outcome.success && !!outcome.result)) {
          const msg = rule.message || `${fieldName} failed validation`;
          if (rule.level === 'warning') {
            result.warnings.push(msg);
          } else {
            errors.push(msg);
          }
        }
      }
    }

    if (errors.length > 0) {
      result.fieldErrors[fieldName] = errors;
    }
  }

  // Record-level validations
  const recordRules = template.recordValidations?.[recordId] || [];
  console.log(`[validateRecordData] Record ${recordId} has ${recordRules.length} validation rules`);
  for (const rule of recordRules) {
    console.log(`[validateRecordData] Checking rule:`, rule);
    // Check if this is a template reference
    // Allow template names with spaces and symbols up to the first '('
    const templateMatch = rule.expr.trim().match(/^@(.+?)\s*\((.*)\)\s*$/);
    if (templateMatch) {
      const [, rawTemplateName, rawParamsStr] = templateMatch;
      console.log(`[validateRecordData] Found template reference: ${rawTemplateName}`);
      const templateName = rawTemplateName.trim();
      const paramsStr = (rawParamsStr || '').trim();
      try {
        // Parse parameters
        const params: Record<string, any> = {};
        if (paramsStr) {
          console.log(`[validateRecordData] Parsing parameters: ${paramsStr}`);
          const paramPairs = paramsStr.split(',');
          for (const pair of paramPairs) {
            const [key, value] = pair.split('=').map(s => s.trim());
            console.log(`[validateRecordData] Evaluating param ${key} = ${value}`);
            const paramResult = await safeEvaluateExpression(value, baseContext, recordScopes);
            params[key] = paramResult.success ? paramResult.result : value;
            console.log(`[validateRecordData] Param ${key} evaluated to:`, params[key]);
          }
        }

        const outcome = await evaluateValidationTemplate(
          templateName,
          params,
          template,
          baseContext,
          recordScopes,
          options?.asyncFunctions
        );
        //"checkBalanceAfterTransaction is not defined"
        if (!(outcome.success && !!outcome.result)) {
          let msg: string = "";
          if (outcome.error) {
            msg = `Record ${recordId} error while validating: ${outcome.error || 'Invalid'}`;
          }
          else {
            msg = rule.message || `Record ${recordId} failed validation: ${outcome.error || 'Invalid'}`;
          }
          if (rule.level === 'warning') {
            result.warnings.push(msg);
          } else {
            result.recordErrors.push(msg);
          }
        }
      } catch (err) {
        result.recordErrors.push(`Failed to evaluate template ${templateName}: ${err instanceof Error ? err.message : String(err)}`);
      }
    } else {
      // Regular expression validation
      const outcome = await safeEvaluateExpression(rule.expr, baseContext, recordScopes);
      if (!(outcome.success && !!outcome.result)) {
        const msg = rule.message || `Record ${recordId} failed validation`;
        if (rule.level === 'warning') {
          result.warnings.push(msg);
        } else {
          result.recordErrors.push(msg);
        }
      }
    }
  }

  result.isValid = Object.keys(result.fieldErrors).length === 0 && result.recordErrors.length === 0;
  return result;
};

/**
 * Validates a multi-record form given a virtual record id and a records map
 */
export const validateMultiRecordData = async (
  virtualRecordId: string,
  recordsData: Record<string, Record<string, any>>, // keys like `${virtual}__SpotFX`
  template: TemplateDoc,
  schema: BaseSchema,
  options?: ValidationOptions
): Promise<{ isValid: boolean; results: Record<string, RecordValidationResult>; warnings: string[] }> => {
  const virtual = template.virtualRecords?.[virtualRecordId];
  const aggregateWarnings: string[] = [];
  const results: Record<string, RecordValidationResult> = {};
  if (!virtual?.isMultiRecord || !virtual.multiRecordConfig) {
    return { isValid: true, results: {}, warnings: [] };
  }

  // Build record scopes from provided data
  const scopes: Record<string, Record<string, any>> = {};
  virtual.multiRecordConfig.forEach(config => {
    const key = `${virtualRecordId}__${config.recordName}`;
    scopes[config.recordName] = recordsData[key] || {};
  });
  const recordScopes = createRecordScopes(scopes);

  for (const config of virtual.multiRecordConfig) {
    const key = `${virtualRecordId}__${config.recordName}`;
    const data = recordsData[key] || {};
    const res = await validateRecordData(config.recordName, data, template, schema, recordScopes, options);
    results[key] = res;
    aggregateWarnings.push(...res.warnings);
    //TODO: figure out multi-record.  Only do validation on the first record for now because it keeps breaking (fx-transfer, the transfer part will fail because quantity will not be available until after the transfer part is written to geneva)
    break;
  }

  const isValid = Object.values(results).every(r => r.isValid);
  return { isValid, results, warnings: aggregateWarnings };
};

interface LookupConfigResult {
  tableName: string;
  keyField?: string;
}

// Some Geneva tables need alternate key fields when no explicit lookup config is provided
const DEFAULT_LOOKUP_KEYS: Record<string, string> = {
  LocationAccount: "NAMESORT",
};

/**
 * Gets the lookup configuration for a field from the template's field overrides
 * @param fieldName The field name to check
 * @param recordId The current record ID being processed  
 * @param template Template containing field overrides with lookup configs
 * @returns Lookup route and key field if configured
 */
const getLookupConfig = (
  fieldName: string,
  recordId: string,
  template: TemplateDoc,
  schema?: BaseSchema
): LookupConfigResult | null => {
  // Get field overrides for the current record

  //CRAPPY HACK: this stupid thing is not in the geneva_schema.json because its a catchall apparently for Equity, MediumOfExchange, Bond, etc.  
  if (fieldName === "Investment") {
    return {
      tableName: "Investment",
      keyField: "KEY",
    }
  }

  const recordOverrides = resolveFieldOverridesRecursively(recordId, template);
  const fieldOverride = recordOverrides[fieldName];

  console.log(`[getLookupConfig] Field override:`, fieldOverride);
  // Check if field has lookup configuration
  if (fieldOverride?.lookup?.route) {
    return {
      tableName: fieldOverride.lookup.route,
      keyField: fieldOverride.lookup.key || undefined,
    };
  }

  // Fallback: use field name as table name only if it matches a known record
  const recordDef = schema?.records.find(r => r.record === fieldName);


  //print out all the record names in schema please
  // console.log(`[getLookupConfigCRAP] Record names in schema:`);

  // schema?.records.map(r => r.record).forEach(r => console.log(`[getLookupConfigCRAP] Record name: ${r}`));

  if (recordDef && Array.isArray(recordDef.fields) && recordDef.fields.length > 0) {
    const firstField = recordDef.fields[0];
    return {
      tableName: fieldName,
      keyField: DEFAULT_LOOKUP_KEYS[fieldName] ?? firstField?.xmlTag,
    };
  }

  console.log(`[getLookupConfig] No lookup config found for ${fieldName} in record ${recordId}`);
  return null;
};

/**
 * Resolves lookup fields by fetching complete Geneva records for lookup fields
 * This makes ALL fields from Geneva SELECT * available in expressions
 * @param formData User form data containing lookup field values
 * @param template Template containing field overrides with lookup configs
 * @param recordId The current record ID being processed
 * @param lookupFunction Function to fetch lookup data
 * @returns Enhanced form data with all Geneva lookup data
 */
const resolveLookupFields = async (
  formData: Record<string, any>,
  template: TemplateDoc,
  recordId: string,
  schema: BaseSchema,
  lookupFunction: (tableName: string, keyValue: string, keyField?: string) => Promise<Record<string, any> | null>
): Promise<Record<string, any>> => {
  const enhancedFormData = { ...formData };
  const overrides = resolveFieldOverridesRecursively(recordId, template);

  //the expressions will determine what will be looked up.  for instance, I don't want to look up Portfolios when it's not used in an expression at all.
  const computedExpressions = new Set<string>();

  Object.values(overrides || {}).forEach(override => {
    const expr = override?.computed?.expr;
    if (expr) {
      computedExpressions.add(expr);
    }
  });

  // Process each field that has a value in form data
  for (const fieldName of Object.keys(formData)) {

    console.log(`[resolveLookupFields] Processing field for lookup: ${fieldName}`);
    const fieldValue = formData[fieldName];
    console.log(`[resolveLookupFields] Field value: ${fieldValue}`);
    if (!fieldValue) continue;

    // Get the lookup config for this field
    const lookupConfig = getLookupConfig(fieldName, recordId, template, schema);
    console.log(`[resolveLookupFields] Lookup config:`, lookupConfig);
    if (!lookupConfig?.tableName) continue;
    if (!computedExpressions.size) continue;

    const lookupPrefix = `${fieldName}_`;
    const expressionUsesLookup = Array.from(computedExpressions).some(expr => expr.includes(lookupPrefix));
    if (!expressionUsesLookup) continue;

    try {
      // Fetch the complete Geneva record
      const lookupData = await lookupFunction(lookupConfig.tableName, fieldValue, lookupConfig.keyField);

      if (lookupData) {
        // Add ALL fields from Geneva record to form data using Geneva's native field names
        // This makes every field available in expressions (UDF_CCY, DESCRIPTION, NAMESORT, etc.)
        Object.keys(lookupData).forEach(genevaFieldName => {
          const fieldKey = `${fieldName}_${genevaFieldName}`;
          enhancedFormData[fieldKey] = lookupData[genevaFieldName];
        });

        console.log(`Added ${Object.keys(lookupData).length} Geneva fields for ${fieldName}:`, Object.keys(lookupData));
      }
    } catch (error) {
      console.warn(`Failed to fetch Geneva lookup data for ${fieldName} from ${lookupConfig.tableName}:`, error);
      // Continue processing other fields even if one lookup fails
    }
  }

  return enhancedFormData;
};

/**
 * Generates a complete Geneva XML-ready object with all defaults and computed values applied
 * @param recordId The record ID (can be virtual or real)
 * @param formData User-entered form data
 * @param template The template document
 * @param schema The base schema
 * @param recordScopes Optional record scopes for multi-record forms
 * @param requestId Optional request ID for auto-generated fields
 * @param rowId Optional row ID for bulk requests
 * @param isUndo Optional flag for undo operations
 * @param multiRecordSuffix Optional suffix for multi-record operations
 * @param lookupFunction Optional function to fetch lookup data on-demand
 * @returns Complete object ready for XML conversion
 */
export const generateCompleteGenevaObject = async (
  recordId: string,
  formData: Record<string, any>,
  template: TemplateDoc,
  schema: BaseSchema,
  recordScopes?: Record<string, Record<string, any>>,
  requestId?: string,
  rowId?: number,
  isUndo?: boolean,
  multiRecordSuffix?: string,
  lookupFunction?: (tableName: string, keyValue: string, keyField?: string) => Promise<Record<string, any> | null>
): Promise<Record<string, any>> => {
  // Resolve lookup fields if a lookup function is provided
  const enhancedFormData = lookupFunction
    ? await resolveLookupFields(formData, template, recordId, schema, lookupFunction)
    : formData;

  // Find the ultimate base record and get all field overrides
  const resolvedOverrides = resolveFieldOverridesRecursively(recordId, template);

  // Find the real record to get field definitions
  let ultimateBaseRecord = null;
  let currentRecordId = recordId;

  // Walk the chain to find the ultimate base record
  while (currentRecordId) {
    const realRecord = schema.records.find(r => r.record === currentRecordId);
    if (realRecord) {
      ultimateBaseRecord = realRecord;
      break;
    }

    // Check if it's a virtual record
    const virtualRecord = template.virtualRecords?.[currentRecordId];
    if (virtualRecord?.base) {
      currentRecordId = virtualRecord.base;
    } else {
      break;
    }
  }

  if (!ultimateBaseRecord) {
    throw new Error(`Cannot find ultimate base record for ${recordId}`);
  }


  const result: Record<string, any> = {};

  // Add LoaderAction and LoaderType fields at the beginning
  result['LoaderAction'] = isUndo ? 'Delete' : 'InsertUpdate';
  result['LoaderType'] = ultimateBaseRecord.record; // Use the base record name, not virtual record

  // Separate regular fields from array fields
  const { regularFields, arrayFieldGroups } = separateArrayFields(ultimateBaseRecord);

  // Process regular fields
  regularFields.forEach(field => {
    if (isUndo && !['KeyValue', 'ExternalSystemReferenceID'].includes(field.xmlTag)) {
      return;
    }
    const fieldOverride = resolvedOverrides[field.xmlTag];

    // Skip hidden fields
    // if (fieldOverride?.hidden) {
    //   return;
    // }

    let fieldValue = enhancedFormData[field.xmlTag];

    // Auto-generate KeyValue, UserTranId1, and ExternalSystemReferenceID
    if (field.xmlTag === 'KeyValue' || field.xmlTag === 'UserTranId1') {
      if (requestId) {
        // Generate KeyValue/UserTranId1: documentID for single, documentID:rowID for bulk
        // For multi-record, append the LoaderType to make it unique
        if (multiRecordSuffix) {
          fieldValue = `${requestId}:${multiRecordSuffix}`;
        } else if (rowId !== undefined) {
          fieldValue = `${requestId}:${rowId}`;
        } else {
          fieldValue = requestId;
        }
      }
    } else if (field.xmlTag === 'ExternalSystemReferenceID') {
      // Generate ExternalSystemReferenceID: firestore path
      // For multi-record, append the LoaderType to make it unique

      if (formData.ExternalSystemReferenceID) {
        fieldValue = formData.ExternalSystemReferenceID;
      }
      else if (requestId) {
        const collection = rowId !== undefined ? 'geneva_bulk_requests' : 'geneva_requests';
        if (multiRecordSuffix) {
          fieldValue = `${collection}/${requestId}:${multiRecordSuffix}`;
        } else if (rowId !== undefined) {
          fieldValue = `${collection}/${requestId}:${rowId}`;
        } else {
          fieldValue = `${collection}/${requestId}`;
        }
      }
    }
    // Apply computed values
    else if (fieldOverride?.computed?.expr) {
      try {
        fieldValue = evaluateComputedExpression(fieldOverride.computed.expr, enhancedFormData, result, recordScopes);
      } catch (error) {
        // Create detailed error message with full context
        const contextSummary = {
          availableFields: Object.keys(enhancedFormData).sort(),
          lookupFields: Object.keys(enhancedFormData).filter(k => k.includes('_')),
          currentResults: Object.keys(result),
          recordScopes: recordScopes ? Object.keys(recordScopes) : [],
          expression: fieldOverride.computed.expr,
          fieldName: field.xmlTag
        };

        console.error(`\n========== COMPUTED EXPRESSION ERROR ==========`);
        console.error(`Field: ${field.xmlTag}`);
        console.error(`Expression: ${fieldOverride.computed.expr}`);
        console.error(`Error: ${error instanceof Error ? error.message : String(error)}`);
        console.error(`\nAvailable Context:`);
        console.error(`- Form fields: ${contextSummary.availableFields.join(', ')}`);
        console.error(`- Lookup fields: ${contextSummary.lookupFields.join(', ') || 'none'}`);
        console.error(`- Already computed: ${contextSummary.currentResults.join(', ')}`);
        if (recordScopes) {
          console.error(`- Record scopes: ${contextSummary.recordScopes.join(', ')}`);
        }
        console.error(`\nForm Data Sample:`);
        // Show first few fields with their values
        const sampleFields = contextSummary.availableFields.slice(0, 10);
        sampleFields.forEach(f => {
          const value = enhancedFormData[f];
          if (typeof value === 'object' && value !== null) {
            console.error(`  ${f}: ${JSON.stringify(value).substring(0, 100)}...`);
          } else {
            console.error(`  ${f}: ${value}`);
          }
        });
        console.error(`===============================================\n`);

        throw new Error(`Failed to evaluate computed expression for field ${field.xmlTag}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    // Apply defaults if no user value provided
    else if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      if (fieldOverride?.default !== undefined) {
        fieldValue = fieldOverride.default;
      }
    }

    // Only include fields that have values (don't include empty fields)
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      result[field.xmlTag] = fieldValue;
    }
  });

  // Process array fields (like AdjLines)
  if (!isUndo) {
    arrayFieldGroups.forEach(arrayGroup => {
      const arrayData = enhancedFormData[arrayGroup.arrayName];

      if (arrayData && Array.isArray(arrayData) && arrayData.length > 0) {
        // Create array in result
        result[arrayGroup.arrayName] = arrayData.map(item => {
          const arrayItem: Record<string, any> = {};

          // Process each field in the array
          arrayGroup.fields.forEach(field => {
            const fieldInfo = getArrayFieldInfo(field);
            if (fieldInfo) {
              // Use composite key for array field overrides to avoid conflicts with regular fields
              const overrideKey = `${arrayGroup.arrayName}.${field.xmlTag}`;
              const fieldOverride = resolvedOverrides[overrideKey];
              let value = item[fieldInfo.fieldName];

              // Apply computed values for array fields
              if (fieldOverride?.computed?.expr) {
                try {
                  // Create context with array item data
                  const arrayContext = { ...enhancedFormData, ...item };
                  value = evaluateComputedExpression(fieldOverride.computed.expr, arrayContext, result, recordScopes);
                } catch (error) {
                  console.error(`\n========== ARRAY FIELD EXPRESSION ERROR ==========`);
                  console.error(`Array: ${arrayGroup.arrayName}`);
                  console.error(`Field: ${field.xmlTag}`);
                  console.error(`Expression: ${fieldOverride.computed.expr}`);
                  console.error(`Error: ${error instanceof Error ? error.message : String(error)}`);
                  console.error(`\nArray Item Data:`);
                  console.error(JSON.stringify(item, null, 2));
                  console.error(`\nAvailable Fields:`);
                  console.error(`- Form fields: ${Object.keys(enhancedFormData).join(', ')}`);
                  console.error(`- Array item fields: ${Object.keys(item).join(', ')}`);
                  console.error(`==================================================\n`);

                  throw new Error(`Failed to evaluate array field expression for ${arrayGroup.arrayName}.${field.xmlTag}: ${error instanceof Error ? error.message : String(error)}`);
                }
              }
              // Apply defaults if no value provided
              else if (value === undefined || value === null || value === '') {
                if (fieldOverride?.default !== undefined) {
                  value = fieldOverride.default;
                }
              }

              // Add to array item using the XML tag name
              if (value !== undefined && value !== null && value !== '') {
                arrayItem[field.xmlTag] = value;
              }
            }
          });

          return arrayItem;
        });
      }
    });
  }

  return result;
};

/**
 * Generates an array of complete Geneva objects for bulk requests
 * @param recordId The record ID (can be virtual or real)
 * @param bulkFormData Array of form data objects (one per row)
 * @param template The template document containing field overrides and virtual records
 * @param schema The base schema containing field definitions
 * @returns Array of complete Geneva objects ready for XML generation
 */
export const generateBulkGenevaObjects = async (
  recordId: string,
  bulkFormData: Record<string, any>[],
  template: TemplateDoc,
  schema: BaseSchema,
  requestId?: string,
  isUndo?: boolean,
  lookupFunction?: (tableName: string, keyValue: string, keyField?: string) => Promise<Record<string, any> | null>
): Promise<Record<string, any>[]> => {
  // Check if this is a multi-record virtual record
  const virtualRecord = template.virtualRecords?.[recordId];
  const isMultiRecord = virtualRecord?.isMultiRecord && virtualRecord?.baseRecords;

  if (isMultiRecord && virtualRecord?.baseRecords && virtualRecord.multiRecordConfig) {
    // Handle multi-record virtual records - each row generates multiple Geneva objects
    const result: Record<string, any>[] = [];

    for (const [rowIndex, rowData] of bulkFormData.entries()) {
      // Create record scopes for cross-record references within this row
      const recordScopesData: Record<string, Record<string, any>> = {};

      // Build record scopes from the row data
      virtualRecord.multiRecordConfig!.forEach((config) => {
        const multiRecordKey = `${recordId}__${config.recordName}`;
        const recordData = rowData[multiRecordKey] || rowData; // Fallback to row data if not nested
        recordScopesData[config.recordName] = recordData;
      });

      const recordScopes = createRecordScopes(recordScopesData);

      // For each row, generate one Geneva object per base record
      const baseRecords: string[] = virtualRecord.baseRecords || [];
      for (const baseRecordName of baseRecords) {
        const config = virtualRecord.multiRecordConfig?.find((c: MultiRecordConfig) => c.recordName === baseRecordName);
        if (!config) continue;

        // Get the field data for this record from the multi-record form data
        // The key format should be: `${recordId}__${baseRecordName}`
        const multiRecordKey = `${recordId}__${baseRecordName}`;
        const recordData = rowData[multiRecordKey] || {};

        // If no specific multi-record data, use the base row data
        // This handles cases where bulk data is structured differently
        const sourceData = Object.keys(recordData).length > 0 ? recordData : rowData;

        // Apply field overrides for this specific record within the multi-record
        const recordOverrides = config.fieldOverrides || {};
        const baseOverrides = template.fieldOverrides[baseRecordName] || {};
        const mergedOverrides = { ...baseOverrides, ...recordOverrides };

        // Create a temporary template for this record
        const tempTemplate: TemplateDoc = {
          ...template,
          fieldOverrides: {
            ...template.fieldOverrides,
            [baseRecordName]: mergedOverrides,
          },
        };

        try {
          // For bulk multi-records, use multiRecordSuffix to make each record unique
          // The rowIndex stays numeric, but we add the record name as a suffix
          const multiRecordSuffix = `${rowIndex}:${baseRecordName}`;

          const genevaObject = await generateCompleteGenevaObject(
            baseRecordName,
            sourceData,
            tempTemplate,
            schema,
            recordScopes, // Pass record scopes for cross-record references
            requestId,
            rowIndex, // Keep rowIndex as number
            isUndo,
            multiRecordSuffix, // Pass combined suffix for uniqueness
            lookupFunction
          );

          // Add metadata to identify the source row and record type
          // genevaObject._bulk_metadata = {
          //   sourceRow: rowIndex + 1,
          //   recordType: baseRecordName,
          //   virtualRecordId: recordId,
          //   isMultiRecord: true,
          // };

          result.push(genevaObject);
        } catch (error) {
          console.error(`Error generating Geneva object for ${baseRecordName} in row ${rowIndex + 1}:`, error);
          // Add a placeholder object with error info
          result.push({
            _error: `Failed to generate ${baseRecordName}`,
            _bulk_metadata: {
              sourceRow: rowIndex + 1,
              recordType: baseRecordName,
              virtualRecordId: recordId,
              isMultiRecord: true,
              error: error instanceof Error ? error.message : 'Unknown error',
            },
          });
        }
      }
    }

    return result;
  } else {
    // Handle regular records (single record per row)
    const regularResult = [];
    for (const [rowIndex, rowData] of bulkFormData.entries()) {
      try {
        const genevaObject = await generateCompleteGenevaObject(
          recordId,
          rowData,
          template,
          schema,
          undefined, // recordScopes
          requestId,
          rowIndex,
          isUndo,
          undefined, // multiRecordSuffix
          lookupFunction
        );

        // Add metadata to identify the source row
        // genevaObject._bulk_metadata = {
        //   sourceRow: rowIndex + 1,
        //   recordType: recordId,
        //   isMultiRecord: false,
        // };

        regularResult.push(genevaObject);
      } catch (error) {
        console.error(`Error generating Geneva object for row ${rowIndex + 1}:`, error);
        // Add a placeholder object with error info
        regularResult.push({
          _error: `Failed to generate record for row ${rowIndex + 1}`,
          _bulk_metadata: {
            sourceRow: rowIndex + 1,
            recordType: recordId,
            isMultiRecord: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
      }
    }

    return regularResult;
  }
};

/**
 * Groups fields that belong to array structures based on their quickImportColumn
 * @param fields Array of BaseField objects
 * @returns Array of ArrayFieldGroup objects
 */
export const groupArrayFields = (fields: BaseField[]): ArrayFieldGroup[] => {
  const arrayGroups = new Map<string, BaseField[]>();

  fields.forEach(field => {
    if (field.quickImportColumn && typeof field.quickImportColumn === 'string' && field.quickImportColumn.includes('.')) {
      const [arrayName] = field.quickImportColumn.split('.');
      if (!arrayGroups.has(arrayName)) {
        arrayGroups.set(arrayName, []);
      }
      arrayGroups.get(arrayName)!.push(field);
    }
  });

  return Array.from(arrayGroups.entries()).map(([arrayName, fields]) => ({
    arrayName,
    fields
  }));
};

/**
 * Identifies if a field belongs to an array structure
 * @param field BaseField object
 * @returns Object with array info or null
 */
export const getArrayFieldInfo = (field: BaseField): { arrayName: string; fieldName: string } | null => {
  if (field.quickImportColumn && typeof field.quickImportColumn === 'string') {
    // Extract only the first line of quickImportColumn to avoid false positives from documentation text
    const firstLine = field.quickImportColumn.split('\n')[0].trim();

    // Check if the first line is a valid array field format (e.g., "ArrayName.FieldName")
    // Exclude fields that are just field names with dots in documentation
    if (firstLine.includes('.') && !firstLine.includes(' ') && !firstLine.startsWith('Note:')) {
      const [arrayName, fieldName] = firstLine.split('.');
      // Additional validation: both parts should be non-empty
      if (arrayName && fieldName) {
        return { arrayName, fieldName };
      }
    }
  }
  return null;
};

/**
 * Separates regular fields from array fields in a record
 * @param record BaseRecord object
 * @returns Object with regular fields and array field groups
 */
export const separateArrayFields = (record: BaseRecord): {
  regularFields: BaseField[];
  arrayFieldGroups: ArrayFieldGroup[];
} => {
  const regularFields: BaseField[] = [];
  const arrayFields: BaseField[] = [];

  record.fields.forEach(field => {
    if (getArrayFieldInfo(field)) {
      arrayFields.push(field);
    } else {
      regularFields.push(field);
    }
  });

  return {
    regularFields,
    arrayFieldGroups: groupArrayFields(arrayFields)
  };
};

/**
 * Creates a default array item with empty values for all fields in the group
 * @param arrayGroup ArrayFieldGroup object
 * @returns Object with field names as keys and empty values
 */
export const createDefaultArrayItem = (arrayGroup: ArrayFieldGroup): Record<string, any> => {
  const item: Record<string, any> = {};

  arrayGroup.fields.forEach(field => {
    const fieldInfo = getArrayFieldInfo(field);
    if (fieldInfo) {
      // Set default value based on field type
      const fieldType = field.type?.toLowerCase();
      switch (fieldType) {
        case 'integer':
        case 'real':
          item[fieldInfo.fieldName] = field.xmlTag.includes('Number') ? 1 : 0;
          break;
        case 'binary':
          item[fieldInfo.fieldName] = false;
          break;
        case 'date':
          item[fieldInfo.fieldName] = '';
          break;
        default:
          item[fieldInfo.fieldName] = '';
      }
    }
  });
  return item;
};

/**
 * Validates array item data
 * @param item Array item data
 * @param arrayGroup ArrayFieldGroup object
 * @returns Validation result with any errors
 */
export const validateArrayItem = (
  item: Record<string, any>,
  arrayGroup: ArrayFieldGroup
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  arrayGroup.fields.forEach(field => {
    const fieldInfo = getArrayFieldInfo(field);
    if (fieldInfo && field.required) {
      const value = item[fieldInfo.fieldName];
      if (value === undefined || value === null || value === '') {
        errors.push(`${field.xmlTag} is required`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export all functions for use in both client and server environments
export default {
  resolveFieldOverridesRecursively,
  resolveMultiRecordFieldOverrides,
  getInheritanceChain,
  recordExists,
  generateCompleteGenevaObject,
  createRecordScopes,
  groupArrayFields,
  getArrayFieldInfo,
  separateArrayFields,
  createDefaultArrayItem,
  validateArrayItem,
  safeEvaluateExpression,
  validateRecordData,
  validateMultiRecordData,
  createPortfolioValidationFunctions,
};





const KUWAIT_OFFSET_MS = 3 * 60 * 60 * 1000;
const EXPECTED_NAV_UTC_HOUR = 3;

const getKuwaitShiftedDate = (date: Date): Date => new Date(date.getTime() + KUWAIT_OFFSET_MS);

const getKuwaitDateParts = (date: Date) => {
  const shifted = getKuwaitShiftedDate(date);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
    dayOfWeek: shifted.getUTCDay(),
  };
};

const getDateFromKuwaitLocalTime = (
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0
): Date => new Date(Date.UTC(year, month, day, hour, minute, second, millisecond) - KUWAIT_OFFSET_MS);

const getExpectedNavTimestampForKuwaitDay = (year: number, month: number, day: number): Date =>
  new Date(Date.UTC(year, month, day, EXPECTED_NAV_UTC_HOUR, 0, 0, 0));

// Helper function to get the NAV processing window for the week
//day index corresponds to this array:  ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export const getNavStartAndEndDateOfWeek = (
  navDate: Date,
  cutoff: CutoffSpec,
  navUpdateDayOfWeek: number
): { startOfWeek: Date; endOfWeek: Date } => {
  const daysBefore = cutoff?.daysBefore ?? 0;
  const cutoffHour = cutoff?.hour ?? 0;
  const cutoffMinute = cutoff?.minute ?? 0;

  const navDayParts = getKuwaitDateParts(navDate);
  const targetDayOfWeek = typeof navUpdateDayOfWeek === 'number' ? navUpdateDayOfWeek : navDayParts.dayOfWeek;
  const currentDayOfWeek = navDayParts.dayOfWeek;

  if (targetDayOfWeek !== currentDayOfWeek) {
    throw new Error(`Nav update day of week is not the same as the current day of week. Nav update day of week: ${targetDayOfWeek}, current day of week: ${currentDayOfWeek}`);
  }

  const endDay = new Date(Date.UTC(navDayParts.year, navDayParts.month, navDayParts.day));
  endDay.setUTCDate(endDay.getUTCDate() - daysBefore);
  const endOfWeek = getDateFromKuwaitLocalTime(
    endDay.getUTCFullYear(),
    endDay.getUTCMonth(),
    endDay.getUTCDate(),
    cutoffHour,
    cutoffMinute,
    0,
    0
  );

  const startDay = new Date(Date.UTC(endDay.getUTCFullYear(), endDay.getUTCMonth(), endDay.getUTCDate()));
  startDay.setUTCDate(startDay.getUTCDate() - 7);
  const startOfWeek = getDateFromKuwaitLocalTime(
    startDay.getUTCFullYear(),
    startDay.getUTCMonth(),
    startDay.getUTCDate(),
    cutoffHour,
    cutoffMinute,
    0,
    0
  );


  return { startOfWeek, endOfWeek };
};

//day index corresponds to this array:  ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export const getExpectedNAVDate = (
  inputDate: Date,
  cutoff: CutoffSpec,
  navUpdateDayOfWeek: number
): Date => {
  const inputDateParts = getKuwaitDateParts(inputDate);
  const candidateDay = new Date(Date.UTC(inputDateParts.year, inputDateParts.month, inputDateParts.day));
  const daysForward = (navUpdateDayOfWeek - inputDateParts.dayOfWeek + 7) % 7;
  candidateDay.setUTCDate(candidateDay.getUTCDate() + daysForward);

  let candidateNavDay = getExpectedNavTimestampForKuwaitDay(
    candidateDay.getUTCFullYear(),
    candidateDay.getUTCMonth(),
    candidateDay.getUTCDate()
  );

  // Get the cutoff boundaries for this candidate NAV day
  let { startOfWeek, endOfWeek } = getNavStartAndEndDateOfWeek(candidateNavDay, cutoff, navUpdateDayOfWeek);

  // If inputDate is past the cutoff (endOfWeek), roll to next week's NAV day
  if (inputDate > endOfWeek) {
    candidateDay.setUTCDate(candidateDay.getUTCDate() + 7);
    candidateNavDay = getExpectedNavTimestampForKuwaitDay(
      candidateDay.getUTCFullYear(),
      candidateDay.getUTCMonth(),
      candidateDay.getUTCDate()
    );
    ({ startOfWeek, endOfWeek } = getNavStartAndEndDateOfWeek(candidateNavDay, cutoff, navUpdateDayOfWeek));
  }

  console.log(`inputDate: ${inputDate.toISOString()}, startOfWeek: ${startOfWeek.toISOString()}, endOfWeek: ${endOfWeek.toISOString()}, candidateNavDay: ${candidateNavDay.toISOString()}`);
  return candidateNavDay;
};
